import React, {useMemo, useState, useContext, useEffect, memo} from 'react';
import {createPortal} from 'react-dom';

/**
 * 폼 기반 JSON 에디터 (최적화 + 접기/펼치기)
 * - 배열/오브젝트: 노드별 접기/펼치기, 전체 접기/펼치기
 * - 레이아웃: 그리드 기반으로 가로 여백 축소, 자식은 펼친 상태에서만 렌더
 * - 성능: React.memo를 통해 프리미티브는 동일값이면 재렌더 방지, 대량 배열은 점진적 렌더
 *
 * props:
 * - initialData: any
 * - handleEdit: (path: (string|number)[], newValue: any) => void
 */

const CollapseContext = React.createContext({ expandSignal: 0, collapseSignal: 0, focus: null });

// 키 이름에 query가 포함된 모든 경로 (오브젝트 키만 대상)
const findKeyPaths = (data, q, path = [], out = []) => {
  if (data && typeof data === 'object') {
    const isArr = Array.isArray(data);
    Object.entries(data).forEach(([k, v]) => {
      const p = [...path, isArr ? Number(k) : k];
      if (!isArr && k.toLowerCase().includes(q)) out.push(p);
      findKeyPaths(v, q, p, out);
    });
  }
  return out;
};

const isPrefixOf = (path, full) => path.length < full.length && path.every((p, i) => p === full[i]);

const DataEditor = ({ initialData, handleEdit, downloadName }) => {
  const [expandSignal, setExpandSignal] = useState(0);
  const [collapseSignal, setCollapseSignal] = useState(0);
  const [query, setQuery] = useState('');
  const [matchIdx, setMatchIdx] = useState(-1);
  const [focus, setFocus] = useState(null); // { path, seq }

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? findKeyPaths(initialData, q) : [];
  }, [initialData, query]);

  const goTo = (step) => {
    if (matches.length === 0) return;
    const next = (matchIdx + step + matches.length) % matches.length;
    setMatchIdx(next);
    setFocus(f => ({ path: matches[next], seq: (f?.seq || 0) + 1 }));
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(initialData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const ctx = useMemo(() => ({ expandSignal, collapseSignal, focus }), [expandSignal, collapseSignal, focus]);

  return (
    <div style={styles.wrapper}>
      {/* 스크롤을 따라다니는 검색 박스 (상위 overflow 영향 피하려고 body에 포털) */}
      {createPortal(
        <div style={styles.floatingSearch}>
          <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setMatchIdx(-1); }}
              onKeyDown={(e) => { if (e.key === 'Enter') goTo(e.shiftKey ? -1 : 1); }}
              placeholder="키 검색 (Enter: 다음, Shift+Enter: 이전)"
              style={{...styles.input, width: 260}}
            />
            {query.trim() && (
              <span style={{color: matches.length ? '#666' : '#ef4444', whiteSpace: 'nowrap'}}>
                {matches.length ? `${matchIdx + 1}/${matches.length}` : '결과 없음'}
              </span>
            )}
            <button style={styles.buttonSm} onClick={() => goTo(-1)} disabled={!matches.length}>▲</button>
            <button style={styles.buttonSm} onClick={() => goTo(1)} disabled={!matches.length}>▼</button>
          </div>
          {focus && (
            <div style={{color: '#666', fontSize: 12, marginTop: 4, wordBreak: 'break-all'}}>
              위치: {focus.path.map(p => typeof p === 'number' ? `[${p}]` : p).join(' › ')}
            </div>
          )}
        </div>,
        document.body
      )}
      <div style={styles.toolbar}>
        <button style={styles.buttonPrimary} onClick={() => setExpandSignal(s => s + 1)}>모두 펼치기</button>
        <button style={styles.buttonSecondary} onClick={() => setCollapseSignal(s => s + 1)}>모두 접기</button>
        {downloadName && (
          <button style={styles.buttonPrimary} onClick={downloadJson}>JSON 다운로드</button>
        )}
      </div>
      <CollapseContext.Provider value={ctx}>
        <MemoNodeEditor data={initialData} path={[]} onEdit={handleEdit} level={0}/>
      </CollapseContext.Provider>
    </div>
  );
};

const getType = (val) => {
  if (Array.isArray(val)) return 'array';
  if (val === null) return 'null';
  return typeof val;
};

const TypeSelect = ({ value, onChange, options = ['text','object','array'] }) => {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={styles.select}>
      {options.map(opt => (
        <option key={opt} value={opt}>
          {opt === 'text' ? '텍스트' : opt === 'object' ? '오브젝트' : '배열'}
        </option>
      ))}
    </select>
  );
};

const NodeEditor = ({ data, path, onEdit, level = 0 }) => {
  const type = getType(data);

  if (type === 'array') {
    return <MemoArrayEditor array={data} path={path} onEdit={onEdit} level={level} />;
  }
  if (type === 'object') {
    return <MemoObjectEditor objectData={data} path={path} onEdit={onEdit} level={level} />;
  }
  return <MemoPrimitiveEditor value={data} path={path} onEdit={onEdit} />;
};
const MemoNodeEditor = memo(
  NodeEditor,
  (prev, next) => prev.data === next.data && prev.onEdit === next.onEdit && prev.level === next.level
);

const PrimitiveEditor = ({ value, path, onEdit }) => {
  const type = getType(value);

  if (type === 'boolean') {
    return (
      <label style={styles.inline}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onEdit(path, e.target.checked)}
          style={{marginRight: 8}}
        />
        {value ? 'true' : 'false'}
      </label>
    );
  }

  if (type === 'number') {
    return (
      <input
        type="number"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onEdit(path, v === '' ? 0 : Number(v));
        }}
        style={styles.input}
      />
    );
  }

  if (type === 'null') {
    return (
      <div style={styles.nullBox}>
        <span style={{marginBottom: 8}}>null</span>
        <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
          <button style={styles.buttonSm} onClick={() => onEdit(path, '')}>텍스트로 설정</button>
          <button style={styles.buttonSm} onClick={() => onEdit(path, false)}>불리언 false</button>
          <button style={styles.buttonSm} onClick={() => onEdit(path, 0)}>숫자 0</button>
        </div>
      </div>
    );
  }

  // default string 또는 기타
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onEdit(path, e.target.value)}
      style={styles.input}
      placeholder="텍스트 입력"
    />
  );
};
const MemoPrimitiveEditor = memo(
  PrimitiveEditor,
  (prev, next) => prev.value === next.value && prev.onEdit === next.onEdit
);

const ArrayEditor = ({ array, path, onEdit, level }) => {
  const { expandSignal, collapseSignal, focus } = useContext(CollapseContext);
  const [expanded, setExpanded] = useState(level < 1); // 루트는 기본 펼침
  const [newType, setNewType] = useState('text');
  const [newText, setNewText] = useState('');
  const initialWindow = 50;
  const [visibleCount, setVisibleCount] = useState(initialWindow);

  useEffect(() => { setExpanded(true); }, [expandSignal]);
  useEffect(() => { setExpanded(false); }, [collapseSignal]);

  useEffect(() => {
    // 데이터 변경 시 보이는 개수 리셋
    setVisibleCount(initialWindow);
  }, [array]);

  // 검색 결과가 이 배열 하위면 펼치고, 해당 인덱스까지 렌더
  useEffect(() => {
    if (!focus || !isPrefixOf(path, focus.path)) return;
    setExpanded(true);
    setVisibleCount(c => Math.max(c, focus.path[path.length] + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  const addItem = () => {
    let newItem;
    if (newType === 'text') newItem = newText;
    if (newType === 'object') newItem = {};
    if (newType === 'array') newItem = [];
    const next = Array.isArray(array) ? array.slice() : [];
    next.push(newItem);
    onEdit(path, next);
    setNewText('');
  };

  const removeItem = (idx) => {
    const next = array.slice();
    next.splice(idx, 1);
    onEdit(path, next);
  };

  const showMore = () => {
    setVisibleCount(c => Math.min(array.length, c + initialWindow));
  };

  const itemsToRender = expanded ? array.slice(0, visibleCount) : [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.caretBtn} onClick={() => setExpanded(e => !e)} aria-label="toggle">
          {expanded ? '▼' : '▶'}
        </button>
        <span style={styles.badge}>배열</span>
        <span style={{color: '#666'}}>길이: {array.length}</span>
      </div>

      {!expanded && (
        <div style={styles.collapsedSummary}>[ ... ]</div>
      )}

      {expanded && (
        <>
          {array.length === 0 && (
            <div style={styles.empty}>비어있는 배열입니다. 아래에서 항목을 추가하세요.</div>
          )}

          <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
            {itemsToRender.map((item, idx) => (
              <div key={idx} style={styles.rowItem}>
                <div style={styles.rowTopBar}>
                  <div style={styles.index}>[{idx}]</div>
                  <button style={styles.buttonDanger} onClick={() => removeItem(idx)}>삭제</button>
                </div>
                <div style={{minWidth: 0}}>
                  <MemoNodeEditor data={item} path={[...path, idx]} onEdit={onEdit} level={level + 1}/>
                </div>
              </div>
            ))}
          </div>

          {visibleCount < array.length && (
            <div style={{marginTop: 6}}>
              <button style={styles.buttonSecondary} onClick={showMore}>
                더 보기 (+{Math.min(initialWindow, array.length - visibleCount)})
              </button>
            </div>
          )}

          <div style={styles.addPanel}>
            <TypeSelect value={newType} onChange={setNewType}/>
            {newType === 'text' && (
              <input
                type="text"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="새 텍스트 값"
                style={styles.input}
              />
            )}
            <button style={styles.buttonPrimary} onClick={addItem}>항목 추가</button>
          </div>
        </>
      )}
    </div>
  );
};
const MemoArrayEditor = memo(
  ArrayEditor,
  (prev, next) => prev.array === next.array && prev.onEdit === next.onEdit && prev.level === next.level
);

const ObjectEditor = ({ objectData, path, onEdit, level }) => {
  const { expandSignal, collapseSignal, focus } = useContext(CollapseContext);
  const [expanded, setExpanded] = useState(level < 1); // 루트는 기본 펼침
  const entries = useMemo(() => Object.entries(objectData || {}), [objectData]);
  const isFocusParent = !!focus && isPrefixOf(path, focus.path);
  const [newKey, setNewKey] = useState('');
  const [newType, setNewType] = useState('text');
  const [newText, setNewText] = useState('');

  // 키별 ref 생성
  const keyRefs = useMemo(() => {
    const refs = {};
    entries.forEach(([key]) => {
      refs[key] = React.createRef();
    });
    return refs;
  }, [entries]);

  useEffect(() => { setExpanded(true); }, [expandSignal]);
  useEffect(() => { setExpanded(false); }, [collapseSignal]);

  // 검색 결과가 이 오브젝트 하위면 펼침
  useEffect(() => {
    if (isFocusParent) setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  // 검색 대상 키가 이 오브젝트의 직속 키면 스크롤 + 하이라이트
  useEffect(() => {
    if (!expanded || !isFocusParent || focus.path.length !== path.length + 1) return;
    const el = keyRefs[focus.path[path.length]]?.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.style.outline = '2px solid #f59e0b';
    const t = setTimeout(() => { el.style.outline = ''; }, 1500);
    return () => { clearTimeout(t); el.style.outline = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, expanded]);

  const deleteProp = (key) => {
    const next = {...objectData};
    delete next[key];
    onEdit(path, next);
  };

  const renameProp = (oldKey, newKeyVal) => {
    const key = String(newKeyVal || '').trim();
    if (!key || key === oldKey) return;
    if (Object.prototype.hasOwnProperty.call(objectData, key)) {
      return;
    }
    const next = {...objectData};
    next[key] = next[oldKey];
    delete next[oldKey];
    onEdit(path, next);
  };

  const addProp = () => {
    const key = String(newKey || '').trim();
    if (!key) return;
    if (Object.prototype.hasOwnProperty.call(objectData, key)) return;

    let value;
    if (newType === 'text') value = newText;
    if (newType === 'object') value = {};
    if (newType === 'array') value = [];
    const next = {...objectData, [key]: value};
    onEdit(path, next);
    setNewKey('');
    setNewText('');
  };

  // 네비게이션 버튼 클릭 시 해당 키로 스크롤
  const handleNavClick = (key) => {
    const ref = keyRefs[key];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 포커스 효과를 주고 싶으면 아래 코드 추가
      ref.current.classList.add('focus-highlight');
      setTimeout(() => ref.current.classList.remove('focus-highlight'), 800);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.caretBtn} onClick={() => setExpanded(e => !e)} aria-label="toggle">
          {expanded ? '▼' : '▶'}
        </button>
        <span style={styles.badge}>오브젝트</span>
        <span style={{color: '#666'}}>프로퍼티: {entries.length}</span>
      </div>

      {/* 네비게이션 버튼 바 */}
      {expanded && entries.length > 0 && (
        <div style={styles.navBar}>
          {entries.map(([key]) => (
            <button
              key={key}
              style={styles.navBtn}
              onClick={() => handleNavClick(key)}
              type="button"
            >
              {key}
            </button>
          ))}
        </div>
      )}

      {!expanded && (
        <div style={styles.collapsedSummary}>{'{ ... }'}</div>
      )}

      {expanded && (
        <>
          {entries.length === 0 && (
            <div style={styles.empty}>비어있는 오브젝트입니다. 아래에서 프로퍼티를 추가하세요.</div>
          )}

          <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
            {entries.map(([key, value]) => (
              <div key={key} ref={keyRefs[key]} style={styles.rowItem}>
                <MemoObjectRow
                  k={key}
                  value={value}
                  path={path}
                  onEdit={onEdit}
                  onDelete={() => deleteProp(key)}
                  onRename={(newK) => renameProp(key, newK)}
                  level={level}
                />
              </div>
            ))}
          </div>

          <div style={styles.addPanel}>
            <input
              type="text"
              placeholder="새 키"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              style={styles.input}
            />
            <TypeSelect value={newType} onChange={setNewType}/>
            {newType === 'text' && (
              <input
                type="text"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="텍스트 값"
                style={styles.input}
              />
            )}
            <button style={styles.buttonPrimary} onClick={addProp}>프로퍼티 추가</button>
          </div>
        </>
      )}
    </div>
  );
};
const MemoObjectEditor = memo(
  ObjectEditor,
  (prev, next) => prev.objectData === next.objectData && prev.onEdit === next.onEdit && prev.level === next.level
);

const ObjectRow = ({ k, value, path, onEdit, onDelete, onRename, level }) => {
  const [tempKey, setTempKey] = useState(k);

  const commitRename = () => {
    if (tempKey !== k) onRename(tempKey);
  };

  return (
    <div style={styles.rowItem}>
      <div style={styles.formGroup}>
        <div style={styles.label}>키</div>
        <input
          type="text"
          value={tempKey}
          onChange={(e) => setTempKey(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); }}
          style={{...styles.input, width: '100%'}}
        />
      </div>
      <div style={styles.formGroup}>
        <div style={styles.label}>값</div>
        <div style={{minWidth: 0}}>
          <MemoNodeEditor data={value} path={[...path, k]} onEdit={onEdit} level={level + 1}/>
        </div>
      </div>
      <div style={{display: 'flex', justifyContent: 'flex-end'}}>
        <button style={styles.buttonDanger} onClick={onDelete}>삭제</button>
      </div>
    </div>
  );
};
const MemoObjectRow = memo(
  ObjectRow,
  (prev, next) =>
    prev.k === next.k &&
    prev.value === next.value &&
    prev.onEdit === next.onEdit &&
    prev.level === next.level
);

const styles = {
  wrapper: {
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, Apple Color Emoji, Segoe UI Emoji',
    fontSize: 14,
    width: '100%',
    maxWidth: 900,
    margin: '0 auto'
  },
  floatingSearch: {
    position: 'fixed',
    top: 180,
    right: 100,
    zIndex: 1000,
    maxWidth: 'calc(100vw - 48px)',
    padding: 8,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    fontSize: 14
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    marginBottom: 8,
    gap: 6
  },
  container: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
    background: '#fff'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6
  },
  badge: {
    display: 'inline-block',
    background: '#eef2ff',
    color: '#3730a3',
    border: '1px solid #c7d2fe',
    borderRadius: 6,
    padding: '1px 6px',
    fontSize: 12,
    fontWeight: 600
  },
  caretBtn: {
    border: '1px solid #d1d5db',
    background: '#fff',
    borderRadius: 6,
    width: 24,
    height: 24,
    lineHeight: '22px',
    textAlign: 'center',
    cursor: 'pointer'
  },
  rowItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '8px',
    border: '1px solid #f0f0f0',
    borderRadius: 6,
    background: '#fafafa'
  },
  rowTopBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8
  },
  index: {
    color: '#888',
    fontSize: 13,
    fontWeight: 500
  },
  addPanel: {
    marginTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6
  },
  input: {
    padding: '6px 8px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    outline: 'none',
    minWidth: 80,
    width: '100%',
    boxSizing: 'border-box'
  },
  select: {
    padding: '6px 8px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#fff',
    minWidth: 80
  },
  label: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
    fontWeight: 500
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  buttonPrimary: {
    padding: '6px 10px',
    border: '1px solid #2563eb',
    background: '#2563eb',
    color: '#fff',
    borderRadius: 6,
    cursor: 'pointer'
  },
  buttonSecondary: {
    padding: '6px 10px',
    border: '1px solid #6b7280',
    background: '#6b7280',
    color: '#fff',
    borderRadius: 6,
    cursor: 'pointer'
  },
  buttonDanger: {
    padding: '6px 10px',
    border: '1px solid #ef4444',
    background: '#ef4444',
    color: '#fff',
    borderRadius: 6,
    cursor: 'pointer'
  },
  buttonSm: {
    padding: '4px 8px',
    border: '1px solid #d1d5db',
    background: '#f9fafb',
    color: '#111827',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12
  },
  inline: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8
  },
  empty: {
    padding: 8,
    color: '#6b7280',
    fontStyle: 'italic'
  },
  nullBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  collapsedSummary: {
    padding: '4px 6px',
    color: '#6b7280',
    fontStyle: 'italic'
  },
  navBar: {
    display: 'flex',
    gap: 4,
    marginBottom: 8,
    overflowX: 'auto',
    paddingBottom: 2
  },
  navBtn: {
    padding: '2px 8px',
    border: '1px solid #d1d5db',
    background: '#f3f4f6',
    color: '#2563eb',
    borderRadius: 6,
    fontSize: 12,
    cursor: 'pointer',
    fontWeight: 500
  }
};

export default DataEditor;
